import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Check, CircleSlash, Clock, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatNumber } from '../lib/format';
import { remoteWorkTone, type RemoteWork, type RemoteWorkDetail } from '../lib/remote-work';
import { Badge, Button, Card, CardHeader, Detail, ErrorState, LoadingBlock, PageHeader, useToast } from '../components/ui';
import { remoteWorkDates } from './RemoteWork';

/**
 * Route: `/remote-work/:id` — one request to work remotely: whose, which days,
 * why; its two approvals one after the other — the team leader's, then an
 * administrator's — and what each said; deciding or cancelling it, for whoever
 * may. Its notifications open here, the phone's on the web.
 */
export function RemoteWorkDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: request, isLoading, error, refetch } = useQuery({
    queryKey: ['remote-work', 'one', id],
    queryFn: () => api.get<RemoteWorkDetail>(`/remote-work/${id}`),
  });

  const act = useMutation({
    mutationFn: (action: 'approve' | 'cancel') => api.post<RemoteWork>(`/remote-work/${id}/${action}`, {}),
    onSuccess: (result, action) => {
      toast.success(
        action === 'cancel'
          ? t('remoteWork.cancelledToast')
          : result.status === 'APPROVED'
            ? t('remoteWork.approvedToast')
            : t('remoteWork.approvedLeaderToast'),
      );
      void queryClient.invalidateQueries({ queryKey: ['remote-work'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !request) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const own = request.userId === user?.id;
  const { can } = request;
  // Up to my requests, to the queue it waits in, or to everything.
  const back = own ? '/remote-work?tab=mine' : can.decide ? '/remote-work?tab=review' : '/remote-work?tab=all';

  return (
    <>
      <div className="mb-4">
        <Link to={back} className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('remoteWork.title')}
        </Link>
      </div>

      <PageHeader
        title={own ? t('remoteWork.title') : localized(request.user)}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={remoteWorkTone(request.status)}>{t(`remoteWork.status.${request.status}` as TranslationKey)}</Badge>
            <span className="tabular">{remoteWorkDates(request, locale)}</span>
          </span>
        }
        action={
          (can.decide || can.cancel) && (
            <>
              {can.decide && (
                <>
                  <Button
                    variant="primary"
                    icon={<Check className="h-4 w-4" />}
                    loading={act.isPending && act.variables === 'approve'}
                    disabled={act.isPending}
                    onClick={() => act.mutate('approve')}
                  >
                    {t('common.approve')}
                  </Button>
                  <Button variant="danger" icon={<X className="h-4 w-4" />} disabled={act.isPending} onClick={() => navigate(`/remote-work/${id}/reject`)}>
                    {t('common.reject')}
                  </Button>
                </>
              )}
              {can.cancel && (
                <Button loading={act.isPending && act.variables === 'cancel'} disabled={act.isPending} onClick={() => act.mutate('cancel')}>
                  {t('remoteWork.cancel')}
                </Button>
              )}
            </>
          )
        }
      />

      {can.decide && (
        <div className="mb-5 rounded-md border border-primary/30 bg-primary/8 px-4 py-2.5 text-body-sm text-[var(--text-primary)]">
          {request.waitingFor === 'LEADER'
            ? t('remoteWork.decideAsLeader')
            : request.leaderSkipped
              ? t('remoteWork.decideNoLeader')
              : // Both its team leader and an administrator: they give both, one after the other.
                request.leaderId === user?.id
                ? t('remoteWork.decideAsAdminToo')
                : t('remoteWork.decideAsAdmin')}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <Card>
            <CardHeader title={t('remoteWork.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('common.employee')}>
                <span className="inline-flex flex-col items-end">
                  <span>{localized(request.user)}</span>
                  {request.department && <span className="text-caption text-muted">{localized(request.department)}</span>}
                </span>
              </Detail>
              <Detail label={t('remoteWork.dates')}>
                <span className="tabular">{remoteWorkDates(request, locale)}</span>
              </Detail>
              <Detail label={t('remoteWork.days')}>
                <span className="tabular">{formatNumber(request.days, locale)}</span>
              </Detail>
              <Detail label={t('remoteWork.asked')}>
                <span className="tabular">{formatDateTime(request.createdAt, locale)}</span>
              </Detail>
              {request.cancelledAt && (
                <Detail label={t('remoteWork.cancelledBy')}>
                  <span className="inline-flex flex-col items-end">
                    <span>{request.cancelledBy ? localized(request.cancelledBy) : '—'}</span>
                    <span className="text-caption text-muted tabular">{formatDateTime(request.cancelledAt, locale)}</span>
                  </span>
                </Detail>
              )}
            </dl>
            <div className="mt-4 border-t hairline pt-4">
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">{t('common.reason')}</p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">{request.reason}</p>
            </div>
          </Card>
        </div>

        <div className="min-w-0">
          <ApprovalsCard request={request} />
        </div>
      </div>
    </>
  );
}

type StepState = 'APPROVED' | 'REJECTED' | 'WAITING' | 'LATER' | 'SKIPPED' | 'NONE';

/** What became of one approval, from the request as it stands. */
function stepState(request: RemoteWorkDetail, step: 'LEADER' | 'ADMIN'): StepState {
  const decidedAt = step === 'LEADER' ? request.leaderDecidedAt : request.adminDecidedAt;
  if (decidedAt) return request.status === 'REJECTED' && (step === 'ADMIN' || !request.adminDecidedAt) ? 'REJECTED' : 'APPROVED';
  if (step === 'LEADER' && request.leaderSkipped) return 'SKIPPED';
  if (request.waitingFor === step) return 'WAITING';
  // The first still to come, or the request settled without it: rejected earlier, or withdrawn.
  return step === 'ADMIN' && request.waitingFor === 'LEADER' ? 'LATER' : 'NONE';
}

const STEP_ICONS: Record<StepState, typeof Check> = {
  APPROVED: Check,
  REJECTED: X,
  WAITING: Clock,
  LATER: Clock,
  SKIPPED: CircleSlash,
  NONE: CircleSlash,
};

/**
 * The two approvals, one under the other — the team leader's, then an
 * administrator's: who gave each and when, what they said, or who it waits for.
 */
function ApprovalsCard({ request }: { request: RemoteWorkDetail }) {
  const { t, locale, localized } = useI18n();
  const steps = (['LEADER', 'ADMIN'] as const).map((step) => {
    const state = stepState(request, step);
    const who = step === 'LEADER' ? request.leader : request.admin;
    const at = step === 'LEADER' ? request.leaderDecidedAt : request.adminDecidedAt;
    const note = step === 'LEADER' ? request.leaderNote : request.adminNote;
    const name = who ? localized(who) : '—';
    let text: ReactNode;
    if (state === 'APPROVED') text = t('remoteWork.stepApproved', { name });
    else if (state === 'REJECTED') text = t('remoteWork.stepRejected', { name });
    else if (state === 'SKIPPED') text = t('remoteWork.stepSkipped');
    else if (state === 'LATER') text = t('remoteWork.stepAfterLeader');
    else if (state === 'WAITING') {
      const names = step === 'LEADER' ? request.teamLeaders.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ') : '';
      text = names ? t('remoteWork.stepWaitingFor', { names }) : t('remoteWork.stepWaiting');
    } else text = request.status === 'CANCELLED' ? t('remoteWork.stepWithdrawn') : t('remoteWork.stepNotNeeded');
    return { step, state, text, at, note };
  });

  return (
    <Card>
      <CardHeader title={t('remoteWork.steps')} subtitle={t('remoteWork.stepsNote')} />
      <ol className="space-y-4">
        {steps.map(({ step, state, text, at, note }, index) => {
          const Icon = STEP_ICONS[state];
          return (
            <li key={step} className="flex gap-3">
              <span
                className={clsx(
                  'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ring-1 ring-inset',
                  state === 'APPROVED' && 'bg-success-bg text-success-fg ring-success/30 dark:bg-success/14 dark:text-success-dark',
                  state === 'REJECTED' && 'bg-error-bg text-error-fg ring-error/30 dark:bg-error/14 dark:text-error-dark',
                  state === 'WAITING' && 'bg-warning-bg text-warning-fg ring-warning/40 dark:bg-warning/14 dark:text-warning-dark',
                  (state === 'LATER' || state === 'SKIPPED' || state === 'NONE') && 'bg-neutral-500/8 text-[var(--text-tertiary)] ring-neutral-500/25',
                )}
                aria-hidden
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0">
                <p className="text-body-sm font-medium text-[var(--text-primary)]">
                  <span className="tabular">{formatNumber(index + 1, locale)}.</span> {t(`remoteWork.step.${step}` as TranslationKey)}
                </p>
                <p className="text-body-sm text-muted">{text}</p>
                {at && <p className="text-caption text-faint tabular">{formatDateTime(at, locale)}</p>}
                {note && (
                  <p className="mt-1 whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                    “{note}”
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
